import { Button } from 'lib/components';
import React, { useEffect, useState } from 'react';
import ReactDom from 'react-dom';
import { HospitalService, JobService } from 'types';
import classes from '../index.module.scss';
import ServiceSelect from './ServiceSelect';

/**
 * Props interface for the ServiceInputModal component
 */
interface Props {
	/** Array of available hospital services to select from */
	items: Array<HospitalService>;
	/** Whether the modal is currently open */
	isOpen: boolean;
	/** Function to set the modal's open state */
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	/** Callback function when the modal is closed */
	onClose: () => void;
	/** Callback function to add a new service */
	addService: (service: JobService) => void;
	/** Array of service IDs that are already selected */
	selected: Array<number>;
}

/**
 * ServiceInputModal component
 * Provides a modal dialog for adding services to a job
 * Allows selecting a service from available hospital services
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element | null} - Rendered component or null if not open
 */
const ServiceInputModal = ({ items, selected, isOpen, setIsOpen, addService, onClose }: Props) => {
	// State for the selected service ID
	const [serviceId, setServiceId] = useState<number | undefined>();
	// State for the service name
	const [name, setName] = useState<string | undefined>();
	// State for the service description
	const [description, setDescription] = useState<string | undefined>();

	/**
	 * Update description when a service is selected
	 * Fetches the description from the selected service
	 */
	useEffect(() => {
		if (!serviceId) return;
		const service = items.find((item) => item.id === serviceId);
		if (service) {
			setDescription(service.service.description);
		}
	}, [serviceId]);

	// Don't render anything if the modal is closed
	if (!isOpen) {
		return null;
	}

	// Render the modal using a portal to ensure it appears above other content
	return ReactDom.createPortal(
		<div className={classes['add-service-modal']}>
			<div className={classes['add-service-modal-content']}>
				{/* Service selector area */}
				<div className={classes['add-service-modal-content--main']}>
					{/* Service dropdown selector */}
					<ServiceSelect
						items={items}
						selected={selected}
						value={serviceId}
						setValue={setServiceId}
						setLabel={setName}
					/>
				</div>
				{/* Action buttons */}
				<div className={classes['add-service-modal-content--footer']}>
					{/* Add Service button */}
					<Button
						variant="standard"
						title={'Add Service'}
						onClick={() => {
							if (name && description && serviceId) {
								// Add the selected service with its description
								addService({ description, serviceId, name });
								// Reset form values and close modal
								setServiceId(undefined);
								setName(undefined);
								setDescription(undefined);
								setIsOpen(false);
							}
						}}
						// Disable button if any required field is empty
						disabled={!(name && description && serviceId)}
					/>
					{/* Cancel button */}
					<Button color="gray" variant="standard" title="Cancel" onClick={onClose} />
				</div>
			</div>
		</div>,
		// Mount the portal in the designated element in the DOM
		document.getElementById('portal') as HTMLElement
	);
};

export default ServiceInputModal;
